import { FineLevelOffset, LoggingLevel, LoggingMessage, SectionLoggerMethod } from "./types";

/**
 * Rozhraní umožňující logování nezávislé na prostředí.
 * Instance rozhraní jsou vztaženy ke konkrétní oblasti MultiWebu. Viz {@link LoggingSectionType}.
 *
 * Jde o obálku nad {@link ILogger}, která logování usnadňuje. (Nemusí být pro zalogování předáváno tolik parametrů.)
 *
 * ## Práh logování
 * Implementace by také měly zajistit práh logování.
 * Tj. každá instance musí nějak tento práh získat - a logy s nižší závažností (tj. s vyšším číslem {@link LoggingLevelType}) zahodit.
 *
 * -
 *
 * ## POZOR na neserializovatelné argumenty
 * Viz {@link ILogger}.
 *
 * -
 *
 * ## Substituce
 * `message` může obsahovat substituční znaky, které budou nahrazeny za vhodnou reprezentaci odpovídajícího substitučního argumentu.
 * - `%s` - Převede na string - např. přes `arg.toString()`.
 * - `%d` - Indikuje, že argument by měl být (celé) číslo. To převede na string. Pokud je argument desetinné číslo, pak desetinnou část zahodí. Pokud argument číslo není, pak `NaN`.
 * - `%i` - Stejně jako `%d`.
 * - `%f` - Indikuje, že argument by měl být (desetinné) číslo. To převede na string. Pokud argument číslo není, pak `NaN`.
 * - '%%' - Vypíše `%`. K tomuto není žádný odpovídající argument. Např. `log("ahoj %%s %d",10)` -> `ahoj %s 10`.
 *
 * Do {@link ILogger} bude předána již nahrazená zpráva a pouze zbylé argumenty, nepoužité pro nahrazení.
 * - Příklad: `sectionLogger.info("ahoj %s, jak se máš", "Michale", "Tomáši", "Klárko"); --> ILogger.log(<section>, <level.info>, "ahoj Michale, jak se máš", "Tomáši", "Klárko");`
 *
 * (Pozn. _console.log_ má i substituční znaky `%o` a `%O`, ale ty se záměrně nepodporují, neboť nejsou převoditelné na string.)
 *
 * Nepodporované substituční znaky budou do výsledné zprávy přepsány 1:1. Např. `log("muj objekt %o", myObject)` -> `"muj objekt %o"`.
 *
 * @includeToDoc
 */
export interface ISectionLogger {

    /**
     * Zaloguje s úrovní {@link LoggingLevel.error}. Resp. nejdřív zkontroluje práh logování - a zaloguje pouze, pokud je {@link LoggingLevel.error} povolen.
     */
    error: SectionLoggerMethod;

    /**
     * Analogicky jako {@link error}.
     */
    debug: SectionLoggerMethod;

    /**
     * Analogicky jako {@link error}.
     */
    info: SectionLoggerMethod;

    /**
     * Alias pro {@link info}.
     */
    log: SectionLoggerMethod;

    /**
     * Analogicky jako {@link error}.
     */
    warn: SectionLoggerMethod;

    /**
     * Zaloguje s úrovní `mainLevel + fineLevelOffset`. Resp. nejdřív zkontroluje práh logování - a zaloguje pouze, pokud je logování dané úrovně povoleno.
     *
     * Příklady:
     * - `finaLog(LoggingLevel.warning, 200, "someMessage")` zaloguje s úrovní `2000+200 = 2200` což významově představuje mírnější warning (který má lehký náběh do `info`).
     * - `finaLog(LoggingLevel.error, 999, "someMessage")` zaloguje s úrovní `1000+999 = 1999` což významově představuje velmi slabou chybu, už je to spíše warning.
     *
     * @param mainLevel Hlavní závažnost - slouží pro lepší čitelnost a jako podklad pro výpočet výsledné závažnosti.
     * @param fineLevelOffset Jemné doladění závažnosti. Viz {@link FineLevelOffset}.
     * @param messageOrGenerator Buď přímo zpráva k zalogování, nebo funkce vracející tuto zprávu. Více o zprávě - viz {@link ILogger.log}.
     * @param args Volitelné argumenty pro zalogování. Více viz {@link ILogger.log}.
     */
    fineLog(mainLevel: LoggingLevel, fineLevelOffset: FineLevelOffset, messageOrGenerator: LoggingMessage | (() => LoggingMessage), ...args: any[]): void;

    /**
     * Zaloguje zadanou výjimku se závažností {@link LoggingLevel.error}. K výjimce by měl zalogovat sekci, jméno třídy, message a stackTrace.
     * @param exception Výjimka k zalogování.
     */
    logException(exception: Error): void;

    /**
     * Zaloguje zadanou výjimku analogicky jako {@link logException} ale s explicitně zadanou závažností.
     * Závažnost se zadává analogicky jako u {@link fineLog}.
     * @param mainLevel Hlavní závažnost - slouží pro lepší čitelnost a jako podklad pro výpočet výsledné závažnosti.
     * @param fineLevelOffset Jemné doladění závažnosti. Viz {@link FineLevelOffset}.
     * @param exception Výjimka k zalogování.
     */
    fineLogException(mainLevel: LoggingLevel, fineLevelOffset: FineLevelOffset, exception: Error): void;

    /**
     * Analogicky jako {@link error}. Akorát že místo {@link ILogger.log} provolává {@link ILogger.group}.
     *
     * Resp. Je to trochu komplikovanější - s ohledem na práh logování.
     * - Příkaz `group` nemá vlastní práh logování.
     * - To, zda-li se zaloguje, závisí na obsahu skupiny. Tj. na tom, co se do této skupiny zaloguje.
     * - Proto není možné okamžitě provolat {@link ILogger.group}.
     * - Implementace si musí tento požadavek zapamatovat na případné provedení později.
     * - Pokud - před provedením {@link ILogger.groupEnd} - dojde k zalogování (tj. dostatečná závažnost), pak se musí zpětně provolat {@link ILogger.group}.
     * - Pokud - před provedením {@link ILogger.groupEnd} - k zalogování nedojde (např. proto, že požadavek na zalogování měl malou závažnost), pak se zpětně již nic neprovolává.
     *
     * Příklad1:
     * ```
     * // práh logování === WARNING
     * sectionLogger.group("hlavní zpráva"); // Zatím se nezaloguje - pouze interně poznačí.
     * sectionLogger.info("detail s malou závažností"); // Nezaloguje se - malá závažnost.
     * sectionLogger.warn("detail1"); // Zaloguje se. Ale ještě před tím se zpětně zaloguje `group("hlavní zpráva")`.
     * sectionLogger.warn("detail2"); // Zaloguje se. Nic víc.
     * sectionLogger.groupEnd(); // Provolá `ILogger.groupEnd()`.
     * ```
     *
     * Příklad2:
     * ```
     * // práh logování === WARNING
     * sectionLogger.group("hlavní zpráva"); // Nezaloguje se - pouze interně poznačí.
     * sectionLogger.info("detail s malou závažností"); // Nezaloguje se - malá závažnost.
     * sectionLogger.groupEnd(); // `ILogger.groupEnd()` neprovolá. Pouze zruší interní poznačení pro `group("hlavní zpráva")`.
     * ```
     */
    group: SectionLoggerMethod;

    /**
     * Analogicky jako {@link group}. Akorát že místo {@link ILogger.group} provolává {@link ILogger.groupCollapsed}.
     */
    groupCollapsed: SectionLoggerMethod;

    /**
     * Ukončí aktuální skupinu, pokud nějaká existuje.
     * - Buď provoláním {@link ILogger.groupEnd}, pokud aktuální skupina byla zalogována.
     * - Nebo pouze zrušením poznačení této skupiny, pokud skupina zalogována nebyla.
     */
    groupEnd: () => void;
}

export class ISectionLogger {
    private static _properties: string[] = [];
    private static _methods = ["debug", "info", "warn", "error", "fineLog", "group", "groupCollapsed", "groupEnd"];

    // zápis kvůli ES5
    public static get [Symbol.hasInstance](): (instance: any) => boolean {
        return (instance: any) => instance != null && this._properties.every((value) => Reflect.has(instance, value)) &&
        this._methods.every(value => typeof instance[value] === "function");
    }
}
